<template>
    <div>
        <CCol sm="12">
            <CCard>
                <CCardBody class="d-flex flex-column">
                    <div class="d-flex flex-column">
                        <!-- Character -->
                        <div v-if="!baseVariation" role="group" class="form-group col-12">
                            <label class=""> {{ $t('characterMandatory') }}</label>
                            <SingleOptionDropdown v-model="variation.characterId" :options="characters" :title="$t('character')" :index="1" :is-valid="genericValidator(variation.characterId, attemptedSubmit) && errorValidator('Character')" :invalid-feedback="errorOutput['Character']"></SingleOptionDropdown>
                        </div>
                        <!-- Name -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('nameMandatory') }}</label>
                            <CInput v-model="variation.name" :is-valid="genericValidator(variation.name, attemptedSubmit) && errorValidator('Name')" :invalid-feedback="errorOutput['Name']"></CInput>
                        </div>
                        <!-- Color -->
                        <div role="group" class="form-group col-12 col-lg-3">
                            <label class=""> {{ $t('colorMandatory') }}</label>
                            <CInput type="color" v-model="variation.color" :is-valid="genericValidator(variation.color, attemptedSubmit) && errorValidator('Color')" :invalid-feedback="errorOutput['Color']"></CInput>
                        </div>
                        <!-- Size -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('sizeMandatory') }}</label>
                            <p class="font-weight-bold font-italic"> 
                                <span>1 - {{$t('verySmall')}}, </span>
                                <span>2 - {{$t('small')}}, </span>
                                <span>3 - {{$t('normal')}}, </span>
                                <span>4 - {{$t('large')}} </span>
                            </p>
                            <CInput type="number" v-model="variation.size" :is-valid="genericValidator(variation.size, attemptedSubmit) && errorValidator('Size')" :invalid-feedback="errorOutput['Size']"></CInput>
                        </div>
                    </div>
                    <!-- API errors that belong to no field above -->
                    <FormApiErrors :fields="['Character', 'Color', 'Name', 'Size']"></FormApiErrors>
                    <!-- Action button + submitting -->  
                    <!-- Remove v-if to activate edit -->
                    <div role="group" class="form-group col-12 d-flex justify-content-end">
                        <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                        <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                    </div>
                    <!-- Loading -->
                    <FormLoading v-if="loading"></FormLoading>
                </CCardBody>
            </CCard>
        </CCol>
        <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
    </div>
</template>
     
<script>
import FormApiErrors from '@/controls/FormApiErrors.vue';
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import Variation from "@/classes/Variation";
import { mapState, mapActions } from 'vuex'
import LanguageDropdown from "@/controls/LanguageDropdown.vue";
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import FormLoading from '@/controls/FormLoading.vue'
import CustomPicker from '@/controls/CustomPicker.vue'
import InputGetList from "@/classes/InputGetList";
import SingleOptionDropdown from '@/controls/SingleOptionDropdown.vue'

    export default {
    components: { FormApiErrors, LanguageDropdown, ConfirmationPopUp, FormLoading, CustomPicker, SingleOptionDropdown},
    props:{    
        baseVariation:
        {
            type: Object,
            default: null,
        }
    },
    data()
    {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            variation: new Variation(),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage: "",
            loading: false,
        }
    },
    mounted: function()
    {
        this.loadCharacters();
        this.resetVariation();
    },
    computed:
    {
        ...mapState('characters', ['getCharactersOutput']),
        charactersInfo: function() {
            return this.getCharactersOutput ? this.getCharactersOutput : null;
        },
        characters: function()
        {
            var result = this.charactersInfo && this.charactersInfo.data ? this.charactersInfo.data : [];

            return result.map(x => { return { key: x.id.toString(), label: x.name } });
        },
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = [];

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate required fields
            if(!this.variation.characterId) { result['Character'] = this.$i18n.t("validateRequiredField"); }
            if(!this.variation.name) { result['Name'] = this.$i18n.t("validateRequiredField"); }
            if(!this.variation.size) { result['Size'] = this.$i18n.t("validateRequiredField"); }
            if(!this.variation.color) { result['Color'] = this.$i18n.t("validateRequiredField"); }

            return result;
        },
        ...mapState('variations', ['putVariationOutput']),
        ...mapState('variations', ['postVariationOutput']),
    },
    methods:{
        ...mapActions('characters', ['getCharacters']),
        loadCharacters()
        {
            this.loading = true;
            this.getCharacters(new InputGetList(999, 1, '', 1, 'Name'));
        },
        resetVariation(){
            if(!this.baseVariation)
            {
                this.variation = new Variation();
                if(this.$route.params && this.$route.params.characterId)
                {
                    this.variation.characterId = this.$route.params.characterId;
                }
            }
            else
            {
                this.variation = deepClone(new Variation, this.baseVariation);  
                this.variation.characterId = this.variation.characterId.toString();        
            }
        },
        submitClicked(){
            this.attemptedSubmit = true;

            // Emit event
            if(this.variation.isValid())
            {
                this.submitting = true;
                delete this.variation.character;
                this.$emit('submit-requested', this.variation);
            }
        },
        genericValidator,
        errorValidator,
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            this.$router.push({name: "VariationsList" });
        },
    },
    watch:
    {
        putVariationOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        postVariationOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        baseVariation: function()
        {
            this.resetVariation();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        },
        characters: function(val)
        {
            this.loading = false;
        },
    }
}
</script> 
    
<style scoped>

    .showDropdown
    {
        display: block !important;
    }

    .input-group-append
    {
        height: fit-content;
    }

    img
    {
        width: 50%;
        height: 50%;
    }

</style> 